import { Base } from "../../framework/base";
import { BaseFormModel, IBaseFormModel, IBaseSchemaParseResult } from "../common/baseFormModel";
import { totalTaskDataRowId } from "../consts";
import { ChartValueFormatting, TaskChartGroupingFieldGroupingType, TaskChartValueFieldCalculationType, TaskDataRowDataType, TaskDataRowTotalType, TaskLockingType, TaskRepetitionType, TaskVisibility, TaskChartGroupingFieldDynamicValueFieldType } from "../enums";
import { IReportChartDataSet, ReportChartDataSet } from "../report/reportChartDataSet";
import { Translations } from "../translations";
import { ITaskDataRow, TaskDataRow } from "./taskDataRow";
import { TaskDataRowCol } from "./taskDataRowCol";
import { getSchemaObjectFromTaskTypeChart, ITaskTypeChart, TaskTypeChart } from "./taskTypeChart";
import { getSchemaObjectFromTaskTypeCol, ITaskTypeCol, TaskTypeCol, taskTypeColJsonSerializerReplacer } from "./taskTypeCol";
import { ITaskTypePage, TaskTypePage } from "./taskTypePage";

interface IChartGroupDictionary {
  [key: string]: {
    sortValue: string;
    dynamicValueFieldType: TaskChartGroupingFieldDynamicValueFieldType;
    baseName: string;
    baseNameSuffixes: string[];
    canResetGroupFieldName: boolean;
    dataSets: {
        id: string;
        valueFieldIndex: number;
        calculationType: TaskChartValueFieldCalculationType;
        field: string;
        title: string;
        titleSuffix: string;
        chartValueFormatting: ChartValueFormatting;
        showDataLabels: boolean;
        data: number[];
    }[];
};
}

interface ITaskTypeReportChartDataSet extends IReportChartDataSet {
    id: string;
    field: string;
    sortValue: string;
}

export interface ITaskTypeChartData {
    labels: string[];
    yAxisValueFormatting: ChartValueFormatting;
    dataSets: IReportChartDataSet[];
}

export interface ISchemaParseResult extends IBaseSchemaParseResult {
    taskType: ITaskEditTypeItem;
    headerDataRow: ITaskDataRow;
    chartTestDataRows: ITaskDataRow[];
    lineDataRow: ITaskDataRow;
}

export interface ITaskEditTypeItem extends IBaseFormModel {
    group: string;
    taskRepetitionType: TaskRepetitionType;
    taskLockingType: TaskLockingType;
    visibility: TaskVisibility;
    lineMultiselect: boolean;
    headerCols: ITaskTypeCol[];
    lineCols: ITaskTypeCol[];
    pages: ITaskTypePage[];
    charts: ITaskTypeChart[];

    hasFilterColumns(): boolean;
    hasColumnsInitializedFromOtherDataRows(): boolean;
    isVisible(isPrivate: boolean): boolean;
}

export class TaskEditTypeItem extends BaseFormModel implements ITaskEditTypeItem {
    group: string;
    taskRepetitionType: TaskRepetitionType;
    taskLockingType: TaskLockingType;
    visibility: TaskVisibility;
    lineMultiselect: boolean;
    headerCols: TaskTypeCol[];
    lineCols: TaskTypeCol[];
    pages: TaskTypePage[];
    charts: TaskTypeChart[];

    constructor();
    constructor(obj: IBaseFormModel);
    constructor(obj: ITaskEditTypeItem);
    constructor(obj?: any) {
        super(obj);
        this.group = obj && obj.group || "";
        this.taskRepetitionType = obj && obj.taskRepetitionType || TaskRepetitionType.NotRepeated;
        this.taskLockingType = obj && obj.taskLockingType || TaskLockingType.NoLocking;
        this.visibility = obj && obj.visibility || TaskVisibility.PrivateAndPublic;
        this.lineMultiselect = obj && obj.lineMultiselect || false;
        this.headerCols = Base.getTypedArray<TaskTypeCol>(obj ? obj.headerCols : null, TaskTypeCol);
        this.lineCols = Base.getTypedArray<TaskTypeCol>(obj ? obj.lineCols : null, TaskTypeCol);
        this.pages = Base.getTypedArray<TaskTypePage>(obj ? obj.pages : null, TaskTypePage);
        this.charts = Base.getTypedArray<TaskTypeChart>(obj ? obj.charts : null, TaskTypeChart);
    }

    hasFilterColumns(): boolean {
        return this.hasFilterColumnsBase(this.headerCols) || this.hasFilterColumnsBase(this.formCols);
    }
    
    hasColumnsInitializedFromOtherDataRows(): boolean {
        if (this.taskRepetitionType !== TaskRepetitionType.Repeated) return false;
        for (const col of this.formCols) {
            if (!col.defaultExpression) continue;
            const values = col.defaultExpression.split("#");
            if (values.length > 2) {
                if (values[0].equalIgnoreCase("row")) {
                    return true;
                }
            }
        }
        return false;
    }

    isVisible(isPrivate: boolean): boolean {
        return this.visibility === TaskVisibility.PrivateAndPublic || (isPrivate && this.visibility === TaskVisibility.Private || !isPrivate && this.visibility === TaskVisibility.Public);
    }

    static getTaskTypeFromDataSchema(dataSchema: string, errorCallback: (error) => void): TaskEditTypeItem {
        const result = new TaskEditTypeItem();
        if (!dataSchema) return result;
        try {
            const obj = JSON.parse(dataSchema);
            if (Base.isNullOrUndefined(obj)) return result;
            if (Object.prototype.hasOwnProperty.call(obj, "header") && !Base.isNullOrUndefined(obj.header)) {
                result.headerCols = TaskTypeCol.getJsonTaskTypeColsFromJsonSchema(obj.header, false);
            }
            if (Object.prototype.hasOwnProperty.call(obj, "form") && !Base.isNullOrUndefined(obj.form)) {
                result.formCols = TaskTypeCol.getJsonTaskTypeColsFromJsonSchema(obj.form, false);
            }
            if (Object.prototype.hasOwnProperty.call(obj, "line") && !Base.isNullOrUndefined(obj.line)) {
                result.lineCols = TaskTypeCol.getJsonTaskTypeColsFromJsonSchema(obj.line, true);
            }
            if (Object.prototype.hasOwnProperty.call(obj, "chart") && !Base.isNullOrUndefined(obj.chart)) {
                result.charts = TaskTypeChart.getTaskTypeChartsFromJsonSchema(obj.chart);
            }
        } catch (error) {
            errorCallback(error);
            return null;
        }
        return result;
    }

    static getTaskTypesByVisibility = (taskTypes: ITaskEditTypeItem[], isPublic: boolean): ITaskEditTypeItem[] => {
        return isPublic
            ? taskTypes.filter(i => i.visibility === TaskVisibility.Public || i.visibility === TaskVisibility.PrivateAndPublic)
            : taskTypes.filter(i => i.visibility === TaskVisibility.Private || i.visibility === TaskVisibility.PrivateAndPublic);
    };

    static getChartTestDataRows = (taskType: ITaskEditTypeItem): ITaskDataRow[] => {
        const result: ITaskDataRow[] = [];
        //Generate basic test data rows
        for (let i = 0; i < 5; i++) {
            const dataRow = new TaskDataRow();
            dataRow.id = Base.getGuid();
            dataRow.cols = TaskDataRowCol.initializeTestValuesByType(taskType.formCols, []);
            dataRow.createDate = Base.getNowUtcDate().addDays(Base.getRandomIntInteger(30)).getTime();
            dataRow.createUserCode = "User" + Base.getRandomIntInteger(10);
            dataRow.createUserName = dataRow.createUserCode;
            dataRow.modifyDate = new Date(dataRow.createDate).addDays(Base.getRandomIntInteger(7)).addHours(Base.getRandomIntInteger(24)).addMinutes(Base.getRandomIntInteger(60)).getTime();
            dataRow.modifyUserCode = "User" + Base.getRandomIntInteger(10);
            dataRow.modifyUserName = dataRow.modifyUserCode;
            TaskEditTypeItem.evaluateColsExpressions(dataRow.cols, taskType.formCols);
            result.push(dataRow);
        }
        TaskDataRow.sortTaskDataRows(result, taskType);
        //Modify test data rows if they initialized from other data rows
        if (taskType.hasColumnsInitializedFromOtherDataRows()) {
            for (let i = 0; i < result.length; i++) {
                const dataRow = result[i];
                if (i > 0) {
                    const defaultColValues = TaskDataRow.getNewRowDefaultValuesFromTaskDataRow(taskType, result[i - 1], result[0].cols);
                    if (defaultColValues.length) {
                        for (const defaultColValue of defaultColValues) {
                            const dataCol = dataRow.cols.find(i => i.field === defaultColValue.field);
                            if (!dataCol) {
                                dataRow.cols.push(defaultColValue);
                            } else {
                                dataCol.valueStr = defaultColValue.valueStr;
                                dataCol.valueNbr = defaultColValue.valueNbr;
                            }
                        }
                        TaskEditTypeItem.evaluateColsExpressions(dataRow.cols, taskType.formCols);
                    }
                }
            }
        }
        return result;
    };

    static getTaskTypeFromSchema = (dataSchema: string): ISchemaParseResult => {
        let dataSchemaParseError = "";
        const taskType = TaskEditTypeItem.getTaskTypeFromDataSchema(dataSchema, (error) => {
            dataSchemaParseError = Base.isString(error)
                ? (error ?? "")
                : (error.message && Base.isString(error.message)
                    ? error.message
                    : JSON.stringify(error));
        });
        if (!taskType) {
            return {
                taskType: null,
                headerDataRow: null,
                formDataRow: null,
                chartTestDataRows: [],
                lineDataRow: null,
                error: dataSchemaParseError,
            };
        }
        taskType.taskRepetitionType = taskType.lineCols.length > 0
            ? TaskRepetitionType.Repeated
            : TaskRepetitionType.NotRepeated;
        const headerDataRow = new TaskDataRow();
        headerDataRow.id = Base.getGuid();
        headerDataRow.cols = TaskDataRowCol.initializeValuesByType(taskType.headerCols, []);
        const formDataRow = new TaskDataRow();
        formDataRow.id = Base.getGuid();
        formDataRow.cols = TaskDataRowCol.initializeValuesByType(taskType.formCols, []);
        const chartTestDataRows = TaskEditTypeItem.getChartTestDataRows(taskType);
        const lineDataRow = new TaskDataRow();
        lineDataRow.id = Base.getGuid();
        lineDataRow.cols = TaskDataRowCol.initializeValuesByType(taskType.lineCols, []);
        return {
            taskType,
            headerDataRow,
            formDataRow,
            chartTestDataRows,
            lineDataRow,
            error: dataSchemaParseError
        };
    };

    static getTaskTypesForUiSelect = (taskTypes: ITaskEditTypeItem[], isPublic: boolean) => {
        const filteredTaskTypes = TaskEditTypeItem.getTaskTypesByVisibility(taskTypes, isPublic);
        const notRepeatedTaskTypes = filteredTaskTypes.filter(i => i.taskRepetitionType !== TaskRepetitionType.Repeated);
        const repeatedTaskTypes = filteredTaskTypes.filter(i => i.taskRepetitionType === TaskRepetitionType.Repeated);
        let result: { id: string, title: string, divider?: boolean }[] = [];
        if (notRepeatedTaskTypes.length > 0) {
            result = [{ id: "NotRepeated", title: Translations.OneTimeForm, divider: true }];
            result = result.concat(
                notRepeatedTaskTypes.map(item => {
                    return {
                        id: item.id,
                        title: item.code
                    };
                }));
        }
        if (repeatedTaskTypes.length > 0) {
            result = result.concat([{ id: "Repeated", title: Translations.RowForm, divider: true }]);
            result = result.concat(
                repeatedTaskTypes.map(item => {
                    return {
                        id: item.id,
                        title: item.code
                    };
                }));
        }
        return result;
    };
    
    //#region Total Row
    static getTotalRowTaskEditTypeItem = (taskType: ITaskEditTypeItem): ITaskEditTypeItem => {
        const result = new TaskEditTypeItem();
        result.id = taskType.id;
        let hasTotalCols = false;
        for (const lineCol of taskType.lineCols) {
            const formCol = taskType.formCols.find(i => i.field === lineCol.field);
            const col = new TaskTypeCol();
            if (!formCol) {
                //Fixed col like ModifyUserName
                col.dataType = lineCol.dataType;
                col.field = lineCol.field;
                col.class = lineCol.class;
                col.decimals = lineCol.decimals;
                col.defaultStr = lineCol.defaultStr;
                col.expression = lineCol.expression;
                col.lineTotalType = lineCol.lineTotalType;
                col.expressionFormat = lineCol.expressionFormat;
                result.formCols.push(col);
                result.lineCols.push(col);
                continue;
            }
            if (lineCol.lineTotalType !== TaskDataRowTotalType.None) {
                hasTotalCols = true;
            }
            col.dataType = lineCol.lineTotalType === TaskDataRowTotalType.None || lineCol.lineTotalType === TaskDataRowTotalType.Text
                ? TaskDataRowDataType.String
                : (lineCol.lineTotalType === TaskDataRowTotalType.LineCount
                    ? TaskDataRowDataType.Integer
                    : (lineCol.dataType === TaskDataRowDataType.Time && lineCol.lineTotalType === TaskDataRowTotalType.ColSum
                        ? TaskDataRowDataType.TimeSpan
                        : lineCol.dataType
                    ));
            col.field = lineCol.field;
            col.class = lineCol.class;
            col.decimals = formCol.decimals;
            col.defaultStr = lineCol.lineTotalType === TaskDataRowTotalType.Text
                ? lineCol.lineTotalExpression
                : "";
            col.expression = lineCol.lineTotalType === TaskDataRowTotalType.Expression
                ? lineCol.lineTotalExpression
                : "";
            col.lineTotalType = lineCol.lineTotalType === TaskDataRowTotalType.None || lineCol.lineTotalType === TaskDataRowTotalType.Text
                ? TaskDataRowTotalType.None
                : lineCol.lineTotalType;
            col.expressionFormat = lineCol.lineTotalType === TaskDataRowTotalType.None || lineCol.lineTotalType === TaskDataRowTotalType.Text
                ? ""
                : formCol.expressionFormat;
            result.formCols.push(col);
            result.lineCols.push(col);
        }
        if (!hasTotalCols) {
            result.formCols = [];
            result.lineCols = [];
        }
        return result;
    };

    static getTotalRow = (taskType: ITaskEditTypeItem, totalTaskType: ITaskEditTypeItem, rows: ITaskDataRow[]): ITaskDataRow => {
        if (!totalTaskType || totalTaskType.lineCols.length === 0) return null;
        const baseTimeStamp = new Date(Date.UTC(1899, 11, 31)).getTime();
        const result = new TaskDataRow();
        result.id = totalTaskDataRowId;
        result.cols = [];
        const colIndexes = rows.length > 0
            ? totalTaskType.lineCols.map(lc => rows[0].cols.findIndex(rc => rc.field === lc.field))
            : [];
        const formColIndexes = rows.length > 0
            ? totalTaskType.lineCols.map(lc => taskType.formCols.findIndex(fc => fc.field === lc.field))
            : [];
        for (let i = 0; i < totalTaskType.lineCols.length; i++) {
            const formColDataType = formColIndexes[i] > -1
                ? taskType.formCols[formColIndexes[i]].dataType
                : -1;
            const totalCol = totalTaskType.lineCols[i];
            const col = new TaskDataRowCol();
            col.field = totalCol.field;
            col.valueNbr = 0;
            col.valueStr = totalCol.dataType === TaskDataRowDataType.String
                ? totalCol.defaultStr
                : "";
            if (totalCol.lineTotalType === TaskDataRowTotalType.LineCount) {
                col.valueNbr = rows.length;
                col.valueStr = col.valueNbr.toString();
            } else if (totalCol.lineTotalType === TaskDataRowTotalType.ColSum || totalCol.lineTotalType === TaskDataRowTotalType.ColAverage && rows.length > 0 && colIndexes[i] >= 0) {
                if (totalCol.dataType === TaskDataRowDataType.Integer ||
                    totalCol.dataType === TaskDataRowDataType.Number ||
                    totalCol.dataType === TaskDataRowDataType.TimeSpan ||
                    totalCol.dataType === TaskDataRowDataType.Date ||
                    totalCol.dataType === TaskDataRowDataType.Time) {
                    col.valueNbr = rows.reduce((a, row) => a + (row.cols[colIndexes[i]].valueNbr ?? 0), 0);
                    if (totalCol.lineTotalType === TaskDataRowTotalType.ColAverage) {
                        col.valueNbr = col.valueNbr / rows.length;
                    } else if (totalCol.dataType === TaskDataRowDataType.Time ||
                        totalCol.dataType === TaskDataRowDataType.Date ||
                        formColDataType === TaskDataRowDataType.Time) {
                        if (totalCol.dataType === TaskDataRowDataType.TimeSpan && formColDataType === TaskDataRowDataType.Time && rows.length > 0 && col.valueNbr !== 0) {
                            col.valueNbr = (col.valueNbr - rows.length * baseTimeStamp) / (1000 * 60 * 60);
                        } else {
                            col.valueNbr = 0;
                        }
                    }
                }
            }
            result.cols.push(col);
        }
        TaskEditTypeItem.evaluateColsExpressions(result.cols, totalTaskType.formCols);
        return result;
    };
    //#endregion Total Row

    //#region Chart
    static getTaskTypeChartData = (taskType: ITaskEditTypeItem, taskTypeChart: ITaskTypeChart, formDataRows: ITaskDataRow[]): ITaskTypeChartData => {
        const result: ITaskTypeChartData = {
            yAxisValueFormatting: ChartValueFormatting.None,
            labels: [],
            dataSets: []
        };
        const groups: IChartGroupDictionary = {};
        //Special type cols for careate user, create date and modify user and modify date
        const implicitTaskTypeCols = TaskTypeCol.getImplicitJsonTaskTypeCols();
        // group chart data and collect group values
        for (const dataRow of formDataRows) {
            const implicitTaskDataRowCols = TaskDataRow.getImplicitTaskDataRowCols(dataRow);
            for (const groupingField of taskTypeChart.groupingFields) {
                const groupCol = dataRow.cols.find(c => c.field === groupingField.field) ?? implicitTaskDataRowCols.find(c => c.field === groupingField.field);
                if (!groupCol) continue;
                const groupTypeCol = taskType.formCols.find(c => c.field === groupingField.field) ?? implicitTaskTypeCols.find(c => c.field === groupingField.field);
                if (!groupTypeCol) continue;
                const uiValue = dataRow.getTaskDataRowColListUiValue(groupingField.field, taskType);
                let groupId = uiValue?.text || "";
                let groupBaseName = uiValue?.text || "";
                let groupBaseNameSuffix = "";
                let groupDynamicValueFieldType = TaskChartGroupingFieldDynamicValueFieldType.None;
                let groupSortValue = null;
                let groupCanResetGroupFieldName = false;
                if (groupTypeCol.dataType === TaskDataRowDataType.Date && groupBaseName) {
                    groupDynamicValueFieldType = groupingField.dynamicValueFieldType ?? TaskChartGroupingFieldDynamicValueFieldType.None;
                    const groupingType = groupingField.groupingType ?? 0;
                    const time = groupBaseName.toDate().getTime();
                    groupCanResetGroupFieldName = groupingType !== TaskChartGroupingFieldGroupingType.DayOfWeek;
                    if (groupingType === TaskChartGroupingFieldGroupingType.DayOfWeek) {
                        groupBaseName = `${Base.timeToWeekdayAbbrStr(time)}`;
                        groupSortValue = Base.padZeros(Base.timeToIsoWeekdayNo(time).toString(10), 2);
                    } else if (groupingType === TaskChartGroupingFieldGroupingType.Week) {
                        groupBaseName = `${Translations.WeekAbbr} ${Base.timeToWeekNoStr(time)}`;
                        groupSortValue = Base.padZeros(Base.timeToWeekNoStr(time), 2);
                    } else if (groupingType === TaskChartGroupingFieldGroupingType.Month) {
                        groupBaseName = Base.timeToMonthNameStr(time);
                        groupSortValue = `${Base.padZeros(Base.timeToMonthStr(time), 2)}`;
                    } else if (groupingType === TaskChartGroupingFieldGroupingType.Quartal) {
                        groupBaseName = `Q${Base.timeToQuarterStr(time)}`;
                        groupSortValue = groupBaseName;
                    } else if (groupingType === TaskChartGroupingFieldGroupingType.HalfYear) {
                        groupBaseName = `H${Base.timeToHalfYearStr(time)}`;
                        groupSortValue = groupBaseName;
                    } else if (groupingType === TaskChartGroupingFieldGroupingType.Year) {
                        groupBaseName = Base.timeToYearStr(time);
                        groupSortValue = groupBaseName;
                    }
                    if (groupDynamicValueFieldType === TaskChartGroupingFieldDynamicValueFieldType.None &&
                        groupingType !== TaskChartGroupingFieldGroupingType.Value &&
                        groupingType !== TaskChartGroupingFieldGroupingType.DayOfWeek &&
                        groupingType !== TaskChartGroupingFieldGroupingType.Year) {
                        groupBaseName = `${groupBaseName} / ${Base.timeToYearStr(time)}`;
                    } else if (!(groupDynamicValueFieldType === TaskChartGroupingFieldDynamicValueFieldType.None &&
                        groupingType === TaskChartGroupingFieldGroupingType.Year)) {
                        groupBaseNameSuffix = ` / ${Base.timeToYearStr(time)}`;
                    }
                    if (groupDynamicValueFieldType === TaskChartGroupingFieldDynamicValueFieldType.None &&
                        groupingType !== TaskChartGroupingFieldGroupingType.Year) {
                        groupSortValue = `${Base.timeToYearStr(time)} ${groupSortValue}`;
                    }
                    groupId = groupBaseName;
                }
                //console.log("groupName", groupId, uiValue);
                if (!groupId) {
                    groupId = `'${Translations.Empty}'`;
                }
                let group = groups[groupId];
                if (!group) {
                    group = {
                        baseName: groupBaseName,
                        baseNameSuffixes: [groupBaseNameSuffix],
                        sortValue: groupSortValue ??
                            (groupDynamicValueFieldType === TaskChartGroupingFieldDynamicValueFieldType.None
                                ? dataRow.getTaskDataRowColSortValue(groupingField.field, taskType)
                                : Base.padZeros(groupBaseName, 10)),
                        dynamicValueFieldType: groupDynamicValueFieldType,
                        canResetGroupFieldName: groupCanResetGroupFieldName,
                        dataSets: []
                    };
                    groups[groupId] = group;
                } else if (!group.baseNameSuffixes.includes(groupBaseNameSuffix)) {
                    group.baseNameSuffixes.push(groupBaseNameSuffix);
                }
                for (let valueFieldIndex = 0; valueFieldIndex < taskTypeChart.valueFields.length; valueFieldIndex++) {
                    const valueField = taskTypeChart.valueFields[valueFieldIndex];
                    const valueTypeCol = taskType.formCols.find(c => c.field === valueField.field);
                    if (!valueTypeCol) continue;
                    const dataSetId = groupDynamicValueFieldType === TaskChartGroupingFieldDynamicValueFieldType.None
                        ? valueField.field
                        : valueField.field + groupBaseNameSuffix;
                    let dataSet = group.dataSets.find(d => d.id === dataSetId);
                    if (!dataSet) {
                        const calculationType = valueField.calculationType ?? 0;
                        dataSet = {
                            id: dataSetId,
                            valueFieldIndex,
                            calculationType,
                            chartValueFormatting: calculationType !== TaskChartValueFieldCalculationType.Count &&
                                (valueTypeCol.dataType === TaskDataRowDataType.TimeSpan ||
                                valueTypeCol.dataType === TaskDataRowDataType.Time ||
                                valueTypeCol.dataType === TaskDataRowDataType.Integer && valueTypeCol.expressionFormat)
                                ? ChartValueFormatting.TimeSpan
                                : calculationType === TaskChartValueFieldCalculationType.Count || valueTypeCol.dataType === TaskDataRowDataType.Integer && !valueTypeCol.expressionFormat
                                    ? ChartValueFormatting.Integer
                                    : ChartValueFormatting.None,
                            field: valueField.field,
                            title: valueField.title || valueTypeCol.title || valueField.field,
                            showDataLabels: valueField.showDataLabels ?? false,
                            titleSuffix: groupBaseNameSuffix,
                            data: []
                        };
                        group.dataSets.push(dataSet);
                    }
                    const dataCol = dataRow.cols.find(c => c.field === valueField.field);
                    let dataValue = dataCol ? dataCol.valueNbr : 0;
                    if (dataCol && dataValue !== 0) {
                        if (valueTypeCol.dataType === TaskDataRowDataType.Integer && valueTypeCol.expressionFormat) { //minutesToDateStr(fieldValue)
                            dataValue = (dataValue < 0 ? -1 : 1) * TaskDataRowCol.getTaskDataRowColUiValue(valueTypeCol, dataCol.valueStr, dataValue).toTimeSpan();
                        } else if (valueTypeCol.dataType === TaskDataRowDataType.Time) {
                            dataValue = Base.utcTimeToTimeSpan(dataValue);
                        }
                    }
                    dataSet.data.push(dataValue);
                }
            }
        }
        //console.log("groups", groups, Object.keys(groups).length);
        // ASSUME ONLY ONE GROUPING FIELD!!! - Sort groups
        const sortOrderIsDesc = taskTypeChart.groupingFields.length > 0 && taskTypeChart.groupingFields[0].sortOrderIsDesc;
        const sortedGroupKeys = Object.keys(groups).sort((a, b) => {
            const groupA = groups[a];
            const groupB = groups[b];
            return (sortOrderIsDesc ? -1 : 1) * Base.strCompare(groupA.sortValue, groupB.sortValue);
        });
        // ASSUME ONLY ONE GROUPING FIELD!!! - group and series names
        const dynamicValueFieldTypeIsUsed = taskTypeChart.groupingFields.length > 0 && taskTypeChart.groupingFields[0].dynamicValueFieldType !== TaskChartGroupingFieldDynamicValueFieldType.None;
        const resetGroupAndValueFieldNames = dynamicValueFieldTypeIsUsed &&
            Base.getUniqueStringItems(sortedGroupKeys.map(k => groups[k].baseNameSuffixes).flat()).length < 2;
        //console.log("dynamicValueFieldTypeIsUsed", dynamicValueFieldTypeIsUsed, resetGroupAndValueFieldNames);
        // create chart data sets
        const reportDatasets: ITaskTypeReportChartDataSet[] = [];
        for (let sortedGroupKeyIndex = 0; sortedGroupKeyIndex < sortedGroupKeys.length; sortedGroupKeyIndex++) {
            const key = sortedGroupKeys[sortedGroupKeyIndex];
            const group = groups[key];
            result.labels.push(!resetGroupAndValueFieldNames || !group.canResetGroupFieldName
                ? group.baseName
                : (group.baseName + group.baseNameSuffixes[0]));
            //console.log("result.labels", resetGroupAndValueFieldNames, group.canResetGroupFieldName, result.labels);
            //Add datasets to reportDatasets
            for (const dataSet of group.dataSets) {
                const typeCol = taskType.formCols.find(c => c.field === dataSet.field);
                let value = 0;
                if (dataSet.data.length > 0) {
                    if (dataSet.calculationType === TaskChartValueFieldCalculationType.Count) {
                        value = dataSet.data.length;
                    } else if (dataSet.calculationType === TaskChartValueFieldCalculationType.Sum) {
                        value = dataSet.data.reduce((acc, curr) => acc + curr, 0);
                    } else if (dataSet.calculationType === TaskChartValueFieldCalculationType.Min) {
                        value = dataSet.data.reduce((acc, curr) => Math.min(acc, curr), Infinity);
                    } else if (dataSet.calculationType === TaskChartValueFieldCalculationType.Max) {
                        value = dataSet.data.reduce((acc, curr) => Math.max(acc, curr), -Infinity);
                    } else if (dataSet.calculationType === TaskChartValueFieldCalculationType.Average) {
                        value = dataSet.data.reduce((acc, curr) => acc + curr, 0) / dataSet.data.length;
                    } else if (dataSet.calculationType === TaskChartValueFieldCalculationType.Median) {
                        const sorted = [...dataSet.data].sort((a, b) => a - b);
                        value = sorted.length % 2 === 1
                            ? sorted[Math.floor(sorted.length / 2)]
                            : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2;
                    }
                }
                if (typeCol.dataType === TaskDataRowDataType.Number) {
                    value = Base.roundToDecimals(value, typeCol.decimals);
                }
                let reportDataSet = reportDatasets.find(ds => ds.id === dataSet.id);
                if (!reportDataSet) {
                    reportDataSet = {
                        id: dataSet.id,
                        field: dataSet.field,
                        sortValue: dataSet.titleSuffix + Base.padZeros(dataSet.valueFieldIndex.toString(10), 5),
                        label: !resetGroupAndValueFieldNames
                            ? (dataSet.title + dataSet.titleSuffix)
                            : dataSet.title,
                        data: new Array(sortedGroupKeys.length).fill(null),
                        chartValueFormatting: dataSet.chartValueFormatting,
                        showDataLabels: dataSet.showDataLabels
                    };
                    reportDatasets.push(reportDataSet);
                }
                reportDataSet.data[sortedGroupKeyIndex] = value;
            }
        }
        // ASSUME ONLY ONE GROUPING FIELD!!! - Sort reportDatasetss when dynamic value fields are used
        if (dynamicValueFieldTypeIsUsed && !resetGroupAndValueFieldNames) {
            reportDatasets.sort((a, b) => {
                return Base.strCompare(a.sortValue, b.sortValue);
            });
        }
        // console.log("reportDatasets", reportDatasets);
        result.dataSets = reportDatasets.map(i => ReportChartDataSet.createReportChartDataSet(i.label, i.data, i.chartValueFormatting, i.showDataLabels));
        if (result.dataSets.length > 0 && result.dataSets.filter(ds => ds.chartValueFormatting === result.dataSets[0].chartValueFormatting).length === result.dataSets.length) {
            result.yAxisValueFormatting = result.dataSets[0].chartValueFormatting;
        }
        // console.log("result", result);
        return result;
    };
    //#endregion Chart
}

export const getSchemaFromTaskType = (taskType: ITaskEditTypeItem) => {
    //console.log("taskType?.charts", taskType?.charts);
    const result = JSON.stringify(
        {
            header: Object.fromEntries(
                taskType?.headerCols?.map(({ field, ...rest }) => [field, getSchemaObjectFromTaskTypeCol(rest)])
            ),
            form: Object.fromEntries(
                taskType?.formCols?.map(({ field, ...rest }) => [field, getSchemaObjectFromTaskTypeCol(rest)])
            ),
            line: Object.fromEntries(
                taskType?.lineCols?.map(({ field, ...rest }) => [field, getSchemaObjectFromTaskTypeCol(rest)])
            ),
            chart: Object.fromEntries(
                taskType?.charts?.map(({ name, ...rest }) => [name, getSchemaObjectFromTaskTypeChart(rest)])
            ),
        },
        taskTypeColJsonSerializerReplacer,
        "\t"
    );
    //console.log("getSchemaFromTaskType", result);
    return result;
};